import { useRef, useState } from 'react';
import { renderMarkdown } from '../markdown';

/**
 * A Markdown textarea with a toolbar and a preview.
 *
 * Not a WYSIWYG. The stored value stays Markdown — the same text the API, the MCP tools
 * and the templates already exchange — so nothing downstream has to learn a new format.
 * The toolbar exists because writing a table by hand is genuinely unpleasant, and the
 * preview because a table is the one thing you cannot check by reading the source.
 */

interface Edit {
  /** Replacement for the whole value. */
  value: string;
  /** Where to leave the cursor, as offsets into the new value. */
  selectionStart: number;
  selectionEnd: number;
}

/** Wrap the selection, or drop a placeholder in and select it. */
function wrap(value: string, start: number, end: number, before: string, after: string): Edit {
  const selected = value.slice(start, end) || 'text';
  const next = value.slice(0, start) + before + selected + after + value.slice(end);
  return {
    value: next,
    selectionStart: start + before.length,
    selectionEnd: start + before.length + selected.length,
  };
}

/** Put a marker at the start of every selected line. */
function prefixLines(value: string, start: number, end: number, marker: string): Edit {
  const lineStart = value.lastIndexOf('\n', start - 1) + 1;
  const lineEnd = value.indexOf('\n', end) === -1 ? value.length : value.indexOf('\n', end);
  const block = value.slice(lineStart, lineEnd) || 'item';
  const prefixed = block
    .split('\n')
    .map((line) => (line.startsWith(marker) ? line : marker + line))
    .join('\n');

  return {
    value: value.slice(0, lineStart) + prefixed + value.slice(lineEnd),
    selectionStart: lineStart,
    selectionEnd: lineStart + prefixed.length,
  };
}

/**
 * A table skeleton, on its own lines.
 *
 * Three columns and one empty row: enough to show the shape without having to delete
 * much. The blank line before matters — a table butted against a paragraph is read as
 * part of that paragraph.
 */
function insertTable(value: string, start: number, end: number): Edit {
  const before = value.slice(0, start);
  const lead =
    before === '' || before.endsWith('\n\n') ? '' : before.endsWith('\n') ? '\n' : '\n\n';
  const table = '| Column | Column | Column |\n| --- | --- | --- |\n|  |  |  |\n';
  const next = before + lead + table + value.slice(end);
  // Cursor on the first header cell, so typing replaces it.
  const caret = before.length + lead.length + 2;
  return { value: next, selectionStart: caret, selectionEnd: caret + 6 };
}

export function MarkdownEditor({
  value,
  onChange,
  onBlur,
  placeholder,
  rows = 6,
  label,
}: {
  value: string;
  onChange: (next: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  rows?: number;
  label?: string;
}) {
  const area = useRef<HTMLTextAreaElement>(null);
  const [preview, setPreview] = useState(false);

  const apply = (edit: (value: string, start: number, end: number) => Edit) => {
    const el = area.current;
    if (!el) return;
    const result = edit(el.value, el.selectionStart, el.selectionEnd);
    onChange(result.value);
    // After React has written the new value, put the cursor where the edit wants it —
    // otherwise it jumps to the end and the next keystroke lands in the wrong place.
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(result.selectionStart, result.selectionEnd);
    });
  };

  const tools: { label: string; title: string; run: () => void }[] = [
    { label: 'B', title: 'Bold', run: () => apply((v, s, e) => wrap(v, s, e, '**', '**')) },
    { label: 'I', title: 'Italic', run: () => apply((v, s, e) => wrap(v, s, e, '*', '*')) },
    { label: 'H', title: 'Heading', run: () => apply((v, s, e) => prefixLines(v, s, e, '## ')) },
    { label: '•', title: 'Bullet list', run: () => apply((v, s, e) => prefixLines(v, s, e, '- ')) },
    {
      label: '☑',
      title: 'Checklist',
      run: () => apply((v, s, e) => prefixLines(v, s, e, '- [ ] ')),
    },
    { label: '⌗', title: 'Table', run: () => apply(insertTable) },
    { label: '<>', title: 'Code', run: () => apply((v, s, e) => wrap(v, s, e, '`', '`')) },
    {
      label: '🔗',
      title: 'Link',
      run: () => apply((v, s, e) => wrap(v, s, e, '[', '](https://)')),
    },
  ];

  return (
    <div className="markdown-editor">
      <div className="row gap markdown-toolbar">
        {tools.map((tool) => (
          <button
            key={tool.title}
            type="button"
            title={tool.title}
            aria-label={tool.title}
            disabled={preview}
            onClick={tool.run}
          >
            {tool.label}
          </button>
        ))}
        <button
          type="button"
          className={preview ? 'active' : ''}
          aria-pressed={preview}
          onClick={() => setPreview((v) => !v)}
        >
          {preview ? 'Edit' : 'Preview'}
        </button>
      </div>

      {preview ? (
        <div className="markdown-preview" data-testid="markdown-preview">
          {value.trim() === '' ? (
            <p className="muted">Nothing to preview yet.</p>
          ) : (
            renderMarkdown(value)
          )}
        </div>
      ) : (
        <textarea
          ref={area}
          value={value}
          rows={rows}
          placeholder={placeholder}
          aria-label={label}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
        />
      )}
    </div>
  );
}
